/**
 * Chat composer hotkey configuration.
 *
 * Add or adjust shortcuts here, then bind through useComposerHotkeys hook.
 * Keep this file as the single configuration source for composer-level hotkeys.
 */
function isModeShortcutModifierPressed(event) {
  return [Boolean(event?.ctrlKey), event?.shiftKey !== true, event?.metaKey !== true].every(Boolean);
}

function isDigitPressed(event, digit) {
  const key = String(event?.key || '').trim().toLowerCase();
  const code = String(event?.code || '').trim();
  return (
    key === String(digit)
    || code === `Digit${digit}`
    || code === `Numpad${digit}`
  );
}

const MODE_SHORTCUT_MODIFIER_LABEL = 'Control';
const MAX_NUMBER_SHORTCUTS = 9;

const CHAT_COMPOSER_HOTKEY_ENTRIES = {
  activate_requirement_mode: Object.freeze({
    id: 'activate_requirement_mode',
    label: '梳理模式',
    triggerLabel: 'Shift + Tab',
    /**
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    match(event) {
      return Boolean(event?.shiftKey) && String(event?.key || '') === 'Tab';
    },
    preventDefault: true,
    stopPropagation: false,
  }),
  dismiss_active_mode: Object.freeze({
    id: 'dismiss_active_mode',
    label: '退出当前模式',
    triggerLabel: 'Esc',
    /**
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    match(event) {
      return [
        String(event?.key || '') === 'Escape',
        event?.ctrlKey !== true,
        event?.metaKey !== true,
        event?.altKey !== true,
      ].every(Boolean);
    },
    preventDefault: true,
    stopPropagation: true,
  }),
  send_message: Object.freeze({
    id: 'send_message',
    label: '发送消息',
    triggerLabel: 'Enter',
    /**
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    match(event) {
      return (
        String(event?.key || '') === 'Enter'
        && event?.shiftKey !== true
      );
    },
    preventDefault: true,
    stopPropagation: false,
  }),
  chooser_submit: Object.freeze({
    id: 'chooser_submit',
    label: '提交选择',
    triggerLabel: 'Enter',
    /**
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    match(event) {
      return String(event?.key || '') === 'Enter';
    },
    preventDefault: true,
    stopPropagation: false,
  }),
  chooser_dismiss: Object.freeze({
    id: 'chooser_dismiss',
    label: '取消选择',
    triggerLabel: 'Esc',
    /**
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    match(event) {
      return String(event?.key || '') === 'Escape';
    },
    preventDefault: true,
    stopPropagation: false,
  }),
};

export const CHAT_COMPOSER_HOTKEYS = Object.freeze(CHAT_COMPOSER_HOTKEY_ENTRIES);

/**
 * Resolve one hotkey entry by id.
 *
 * @param {string} id - Hotkey id.
 * @returns {object|null} Hotkey config or null.
 */
export function getChatComposerHotkey(id) {
  const key = String(id || '').trim();
  if (!key) {
    return null;
  }
  return CHAT_COMPOSER_HOTKEYS[key] || null;
}

function shortcutNumber(value) {
  const resolved = Number(value);
  return Number.isInteger(resolved) && resolved >= 1 && resolved <= MAX_NUMBER_SHORTCUTS
    ? resolved
    : 0;
}

function shortcutEntryModeKey(entry = {}) {
  const action = Object.assign({}, entry.action);
  return String([
    action.target_mode,
    entry.target_mode,
    entry.modeKey,
    entry.key,
    entry.value,
  ].find(Boolean) ?? '').trim();
}

export function getChatComposerNumberShortcutLabel(order) {
  const resolvedOrder = shortcutNumber(order);
  return resolvedOrder ? `${MODE_SHORTCUT_MODIFIER_LABEL} + ${resolvedOrder}` : '';
}

export function createChatComposerNumberHotkey(order, idPrefix = 'ordered_shortcut') {
  const resolvedOrder = shortcutNumber(order);
  if (!resolvedOrder) {
    return null;
  }
  return Object.freeze({
    id: `${idPrefix}_${resolvedOrder}`,
    label: `顺序快捷键 ${resolvedOrder}`,
    triggerLabel: getChatComposerNumberShortcutLabel(resolvedOrder),
    /**
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    match(event) {
      return isModeShortcutModifierPressed(event) && isDigitPressed(event, resolvedOrder);
    },
    preventDefault: true,
    stopPropagation: true,
  });
}

/**
 * Resolve shortcut trigger label by item order in the provided list.
 *
 * @param {string} modeKey - Workspace mode key.
 * @param {Array<object>} orderedEntries - Ordered shortcut source entries.
 * @returns {string} Shortcut label or empty string.
 */
export function getChatComposerModeShortcutLabel(modeKey, orderedEntries = []) {
  const normalizedModeKey = String(modeKey || '').trim();
  if (!normalizedModeKey || !Array.isArray(orderedEntries)) {
    return '';
  }
  const matchedIndex = orderedEntries
    .map((entry) => shortcutEntryModeKey(entry))
    .findIndex((entryModeKey) => entryModeKey === normalizedModeKey);
  return getChatComposerNumberShortcutLabel(matchedIndex + 1);
}
